/**
 * <x-variant-blocks>
 *
 * Upgrades a wrapping block-option row of the variant picker
 * (.variant-picker__option-values with block/block_swatch options, e.g.
 * sizes) to a uniform grid. The column minimum is the widest label at the
 * configured minimum inline padding (--block-option-min-padding-inline,
 * Theme settings → Variant picker → Block options), so:
 *
 *   - as many blocks as fit at minimum padding share one row
 *   - leftover space widens all blocks equally (reads as dynamic padding)
 *   - when the row wraps, wrapped blocks keep exactly the same width as the
 *     blocks in the first row (equal grid tracks) instead of stretching
 *   - a label is never clipped: the column count drops before text overflows
 *
 * Only active in `wrap` mode; the horizontal scroll-area mode keeps its flex
 * layout, as does everything when JS is unavailable (blocks then grow to
 * fill each line — see theme.css).
 *
 * Re-measures on resize, after webfonts load, and when the variant picker
 * replaces its DOM after an option change.
 */

class VariantBlocks extends HTMLElement {
  connectedCallback() {
    this._resizeObserver = new ResizeObserver(() => this._fit());
    this._resizeObserver.observe(this);

    this._mutationObserver = new MutationObserver(() => this._fit());
    this._mutationObserver.observe(this, { childList: true, subtree: true });

    document.fonts?.ready?.then(() => this._fit());
    requestAnimationFrame(() => this._fit());
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._mutationObserver?.disconnect();
  }

  _fit() {
    if (!this.classList.contains('wrap')) {
      this.classList.remove('variant-blocks--grid');
      return;
    }

    if (this.clientWidth === 0) return; // hidden (e.g. closed quick-buy drawer)

    const blocks = [...this.children].filter(
      (child) => child.classList?.contains('block-swatch') && child.getClientRects().length > 0
    );

    if (blocks.length === 0) {
      this.classList.remove('variant-blocks--grid');
      return;
    }

    // Widest block at minimum padding: content (label + optional colour
    // tile) + inner gaps + padding + border. The inner spans stay at content
    // width even when the block itself is stretched, so they are safe to
    // measure in any layout state.
    //
    // The low-stock badge is an absolutely-positioned child: it is out of
    // flow and must not count toward the block's content width (otherwise the
    // wide "Noch N auf Lager" pill would widen every block and drop a column).
    // Excluded when the container opts in via data-lowstock-keep-width (Theme
    // settings → Low stock badge → keep block width). Both the width sum and
    // the inner-gap count use the filtered list so neither is thrown off.
    const keepWidth = this.hasAttribute('data-lowstock-keep-width');
    let maxNeeded = 0;

    for (const block of blocks) {
      const style = getComputedStyle(block);
      let children = [...block.children];
      if (keepWidth) {
        children = children.filter((child) => !child.classList?.contains('variant-swatch-badge--lowstock'));
      }
      let needed =
        (parseFloat(style.paddingInlineStart) || 0) +
        (parseFloat(style.paddingInlineEnd) || 0) +
        (parseFloat(style.borderInlineStartWidth) || 0) +
        (parseFloat(style.borderInlineEndWidth) || 0) +
        Math.max(0, children.length - 1) * (parseFloat(style.columnGap) || 0);

      for (const child of children) needed += child.getBoundingClientRect().width;

      maxNeeded = Math.max(maxNeeded, needed + 2); // rounding slack
    }

    const containerStyle = getComputedStyle(this);
    const innerWidth =
      this.clientWidth -
      (parseFloat(containerStyle.paddingInlineStart) || 0) -
      (parseFloat(containerStyle.paddingInlineEnd) || 0);
    const gap = parseFloat(containerStyle.columnGap) || 0;

    const columns = Math.max(1, Math.min(blocks.length, Math.floor((innerWidth + gap) / (maxNeeded + gap))));

    this.style.setProperty('--block-cols', columns);
    this.classList.add('variant-blocks--grid');
  }
}

if (!window.customElements.get('x-variant-blocks')) {
  window.customElements.define('x-variant-blocks', VariantBlocks);
}
